import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Card, CardContent } from "../../../components/ui/card";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useCatalogueManagement } from "../../Services/CatalogueManagement";
import { isApiError, getApiErrorMessage } from "../../../Utilities/apiResponse";
import { toast } from "react-toastify";

const ProductDetailPage = () => {
  const { id, productId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { getCatalogueItem } = useCatalogueManagement();

  const [product, setProduct] = useState<any>(null);
  const [metaFields, setMetaFields] = useState<any[]>([]);
  const [mainImage, setMainImage] = useState("/Assets/placeholder.svg");
  const [loading, setLoading] = useState(true);

  const parseMetaFields = (item: any) => {
    try {
      const parsed =
        typeof item?.itemDetails === "string"
          ? JSON.parse(item.itemDetails)
          : item?.itemDetails;
      const fields = parsed?.MetaDataFields
        ? Object.values(parsed.MetaDataFields)
        : [];
      setMetaFields(fields as any[]);
    } catch {
      setMetaFields([]);
    }
  };

  useEffect(() => {
    if (!productId) {
      setLoading(false);
      return;
    }

    const loadProduct = async () => {
      setLoading(true);
      try {
        const stateProduct = location.state?.product;
        if (stateProduct?.id === productId) {
          setProduct(stateProduct);
          parseMetaFields(stateProduct);
          setMainImage(stateProduct?.imageUrls?.[0] || "/Assets/placeholder.svg");
        }

        const response = await getCatalogueItem(productId);
        if (isApiError(response)) {
          if (!stateProduct) {
            toast.error(getApiErrorMessage(response));
          }
          return;
        }

        setProduct(response);
        parseMetaFields(response);
        setMainImage(response?.imageUrls?.[0] || "/Assets/placeholder.svg");
      } catch (error) {
        toast.error(getApiErrorMessage(error));
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [productId]);

  const handleBack = () => {
    if (location.state?.catalogTenantId) {
      navigate(`/app/${id}/products/${location.state.catalogTenantId}`);
      return;
    }
    navigate(`/app/${id}/products`);
  };

  const getFieldDisplayValue = (field: any) => {
    let displayValue = field.fieldValue;
    if (field.fieldData?.length > 0) {
      const selected = field.fieldData.find((item: any) => item.code === field.fieldValue);
      displayValue = selected ? selected.description : field.fieldValue;
    }
    return displayValue?.toString() || "-";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="animate-spin h-8 w-8 text-gray-400" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="w-full mx-auto p-6 space-y-4">
        <Button variant="outline" onClick={handleBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Products
        </Button>
        <p className="text-gray-400">Product not found.</p>
      </div>
    );
  }

  return (
    <div className="w-full mx-auto product-detail-page min-h-screen">
      <div className="flex flex-col lg:flex-row min-h-[calc(100vh-4rem)]">
        {/* Left — Images */}
        <div className="lg:w-[40%] bg-background p-6 flex flex-col border-r border-gray-800">
          <div className="flex items-center justify-center flex-1 mb-4">
            <img
              src={mainImage}
              alt={product?.name}
              className="max-h-[600px] w-full object-contain rounded-lg shadow-md"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {(product?.imageUrls || []).length > 0 ? (
              product.imageUrls.map((img: string, index: number) => (
                <button
                  key={index}
                  type="button"
                  className={`flex-shrink-0 border rounded-md overflow-hidden focus:ring-2 focus:ring-purple-500 ${
                    mainImage === img ? "border-purple-500 ring-2 ring-purple-500" : "border-gray-700"
                  }`}
                  onClick={() => setMainImage(img || "/Assets/placeholder.svg")}
                >
                  <img
                    src={img || "/Assets/placeholder.svg"}
                    alt={`${product?.name} ${index + 1}`}
                    className="w-20 h-20 object-cover"
                  />
                </button>
              ))
            ) : (
              <img
                src="/Assets/placeholder.svg"
                alt="No images"
                className="w-20 h-20 object-cover rounded-md border border-gray-700"
              />
            )}
          </div>
        </div>

        {/* Right — Details */}
        <div className="lg:w-[60%] p-6 overflow-y-auto">
          <Button variant="outline" size="sm" onClick={handleBack} className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Products
          </Button>

          <h1 className="text-2xl font-bold text-white mb-2">{product?.name}</h1>
          <p className="text-sm text-gray-400 mb-4">
            SKU: {product?.sku || "-"} | Code: {product?.code || "-"}
          </p>

          <p className="text-sm text-muted-foreground mb-6">
            {product?.description || "No description provided."}
          </p>

          {metaFields.length > 0 && (
            <Card className="bg-black border-gray-800 text-white">
              <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                {metaFields.map((field: any) => (
                  <div key={field.id} className="py-1">
                    <span className="text-sm font-medium text-gray-500">
                      {field.fieldDisplayName || field.fieldName}:
                    </span>
                    <span className="ml-2 text-base font-medium text-white">
                      {getFieldDisplayValue(field)}
                    </span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
